import { FormEvent, KeyboardEvent, useEffect, useRef, useState } from 'react'

import { Button, TextArea } from '@avantstay/backoffice-core'

import * as S from './NoteEditor.styles'

interface NoteEditorProps {
  initialBody: string
  isSaving: boolean
  onCancel: () => void
  onSave: (body: string) => Promise<boolean>
}

export const NoteEditor = ({ initialBody, isSaving, onCancel, onSave }: NoteEditorProps) => {
  const [draft, setDraft] = useState(initialBody)

  const rootRef = useRef<HTMLFormElement>(null)

  const text = draft.trim()
  const canSave = !!text && text !== initialBody.trim() && !isSaving

  const save = async () => {
    if (!canSave) return

    await onSave(draft)
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLFormElement>) => {
    if (event.key === 'Escape') {
      event.stopPropagation()
      if (!isSaving) onCancel()
      return
    }

    if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
      event.preventDefault()
      void save()
    }
  }

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    void save()
  }

  useEffect(function focusTheNote() {
    const field = rootRef.current?.querySelector('textarea')

    field?.focus()
    field?.setSelectionRange(field.value.length, field.value.length)
  }, [])

  return (
    <S.Root ref={rootRef} aria-label="Edit note" onKeyDown={handleKeyDown} onSubmit={handleSubmit}>
      <TextArea
        rows={3}
        value={draft}
        disabled={isSaving}
        onChange={event => setDraft(event.target.value)}
      />

      <S.Actions>
        <Button.Flat type="button" disabled={isSaving} onClick={onCancel}>
          Cancel
        </Button.Flat>
        <Button.Primary type="submit" disabled={!canSave} loading={isSaving}>
          Save
        </Button.Primary>
      </S.Actions>
    </S.Root>
  )
}
